import React from 'react';
import { getBrandImage } from '../../data/brandImages';
import { HOW_IT_WORKS_STEPS, FEATURED_BENEFITS } from '../../data/siteContent';
import { BrandStrip, Crown } from './ClientLayout';

const StepIcon = ({ index }) => {
  const common = 'w-10 h-10 text-maja-red';
  if (index === 0) {
    return (
      <svg className={common} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM6 21v-1a6 6 0 0112 0v1" />
        <path strokeLinecap="round" d="M19 8v4M21 10h-4" />
      </svg>
    );
  }
  if (index === 1) {
    return (
      <svg className={common} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <ellipse cx="12" cy="8" rx="7" ry="3" />
        <path d="M5 8v4c0 1.7 3.1 3 7 3s7-1.3 7-3V8" />
        <path d="M5 12v4c0 1.7 3.1 3 7 3s7-1.3 7-3v-4" />
      </svg>
    );
  }
  if (index === 2) {
    return (
      <svg className={common} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13l3 3m-3-3L9 11M5 8h14l-1 13H6L5 8z" />
      </svg>
    );
  }
  return (
    <svg className={common} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.05 2.93l1.4 4.3h4.52l-3.66 2.66 1.4 4.3-3.66-2.66-3.66 2.66 1.4-4.3-3.66-2.66h4.52l1.4-4.3z" />
    </svg>
  );
};

export const PageHero = ({ image, title, subtitle, script, scripts = [], children, kicker, compact = false }) => (
  <section className={`relative overflow-hidden ${compact ? 'min-h-[140px] md:min-h-[180px]' : 'min-h-[420px] md:min-h-[500px]'}`}>
    <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/15" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
      <div className="max-w-xl text-white">
        <div className="flex items-center gap-3 mb-3">
          <Crown className="w-10 h-10" />
          {kicker}
        </div>
        <h1 className="font-display font-black text-4xl md:text-6xl leading-none uppercase tracking-tight">{title}</h1>
        {subtitle && <p className="mt-4 text-base md:text-lg text-white/90 max-w-md">{subtitle}</p>}
        {children}
      </div>
      <div className="absolute right-8 bottom-10 hidden md:flex flex-col items-end gap-3">
        {(scripts.length ? scripts : script ? [script] : []).map((line) => (
          <p key={line} className="font-script text-3xl text-white max-w-xs text-right drop-shadow">
            {line}
          </p>
        ))}
      </div>
    </div>
  </section>
);

const HomePage = ({ setCurrentView, siteConfig, userProfile }) => {
  const img = (key) => getBrandImage(siteConfig, key);

  return (
    <div>
      <PageHero
        image={img('heroInicio')}
        kicker={
          <span className="font-display font-black text-5xl md:text-6xl leading-none uppercase tracking-tight">
            Maja <span className="text-maja-red">Club</span>
          </span>
        }
        title="Sumá puntos, obtené beneficios y disfrutá más"
        subtitle="Cada vez que disfrutás en Maja Morena, sumás puntos que se transforman en promos, premios y experiencias exclusivas."
        scripts={['Más rico pertenecer', 'Beneficios que vuelven']}
      >
        <div className="mt-8 flex flex-wrap gap-3">
          <button
            onClick={() => setCurrentView(userProfile ? 'canjes' : 'registration')}
            className="h-12 px-6 rounded-full bg-maja-red text-white font-black hover:bg-red-700"
          >
            {userProfile ? 'Ver canjes' : 'Registrarme gratis'} →
          </button>
          <button
            onClick={() => setCurrentView('howItWorks')}
            className="h-12 px-6 rounded-full bg-black/80 border border-white/40 text-white font-black hover:bg-black"
          >
            Cómo funciona →
          </button>
        </div>
        <div className="mt-8 flex flex-wrap gap-6 text-sm text-white/90">
          <span>Más beneficios para vos</span>
          <span>La buena comida siempre une</span>
          <span>Una comunidad que comparte el mismo sabor</span>
        </div>
      </PageHero>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display font-black text-4xl uppercase tracking-tight">¿Cómo funciona?</h2>
          <p className="font-script text-maja-red text-2xl hidden sm:block">Es muy fácil</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {HOW_IT_WORKS_STEPS.map((step, i) => (
            <div key={step.title} className="text-center sm:text-left">
              <div className="flex justify-center sm:justify-start">
                <StepIcon index={i} />
              </div>
              <h3 className="mt-3 font-black">{step.title}</h3>
              <p className="mt-1 text-sm text-gray-600">{step.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-black text-4xl uppercase tracking-tight">Beneficios destacados</h2>
          <button onClick={() => setCurrentView('benefits')} className="text-maja-red font-black text-sm">
            Ver todos los beneficios →
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {FEATURED_BENEFITS.map((item) => (
            <button
              key={item.title}
              onClick={() => setCurrentView('benefits')}
              className="relative h-44 rounded-2xl overflow-hidden text-left group"
            >
              <img
                src={img(item.imageKey)}
                alt={item.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
              <div className="absolute bottom-3 left-3 right-3 text-white">
                <h3 className="font-black text-sm leading-tight uppercase">{item.title}</h3>
                <p className="text-xs text-white/85 mt-1">{item.text}</p>
              </div>
            </button>
          ))}
        </div>
      </section>

      <BrandStrip
        setCurrentView={setCurrentView}
        title="Sumá puntos en cada compra y canjeá experiencias, promos y premios."
        cta="Conocer beneficios"
        target="benefits"
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h2 className="font-display font-black text-3xl uppercase tracking-tight mb-5">Todo lo que necesitás, en un solo lugar</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {[
            { label: 'Mi cuenta', sub: 'Editá tus datos', view: userProfile ? 'profile' : 'login' },
            { label: 'Mis puntos', sub: 'Consultá tu saldo', view: userProfile ? 'history' : 'login' },
            { label: 'Mis canjes', sub: 'Historial y canjes disponibles', view: 'canjes' },
            { label: 'Preguntas frecuentes', sub: 'Resolvé tus dudas', view: 'help' },
            { label: 'Escribinos por WhatsApp', sub: 'Te ayudamos', view: 'help' },
          ].map((item) => (
            <button
              key={item.label}
              onClick={() => setCurrentView(item.view)}
              className="flex items-center justify-between rounded-2xl border border-gray-100 px-5 py-4 hover:border-maja-red text-left"
            >
              <div>
                <p className="font-black">{item.label}</p>
                <p className="text-sm text-gray-500">{item.sub}</p>
              </div>
              <span className="text-gray-400">→</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
export { StepIcon };
